/**
 * Google sign-in: authorisation code + PKCE through the backend, and the
 * token store behind it.
 *
 * The sixth copy of the shape built for OneDrive, Dropbox, Spotify and
 * SoundCloud - soundCloudAuth.ts is the one to read alongside it, because
 * Google shares its one real difference: **the token endpoint is our own
 * backend, not Google.** Google's token endpoint refuses a code exchange
 * from a "Web application" client that carries no client secret, PKCE or
 * not, and this repository is public. So the secret lives in the Azure
 * Functions app and `GoogleToken` forwards the exchange with it added,
 * returning Google's status and `{ error, error_description }` unchanged.
 *
 * What that buys is a refresh token. Until 2026-10 Google was the one
 * provider on the implicit flow (Google Identity Services' token client):
 * access tokens only, an hour each, and renewal meant a real Google window
 * that opened and closed by itself - every hour, in front of whatever the
 * user was doing, and not at all on iOS, where Safari withholds the cookies
 * it needed. Now a sign-in is one trip to Google and back, and renewal is a
 * POST to the backend that nobody sees.
 *
 * Other things Google does that shape this file:
 *
 * - **A refresh token is only issued when a consent screen was shown.** A
 *   returning user whose consent Google remembers gets a code and no
 *   refresh token - on the device they just signed in on, an hour of access
 *   and then the button again. So every sign-in sends `prompt=consent`.
 *   Sign-ins are rare now, so the screen is a fair price for never being
 *   without a refresh token.
 * - **Refresh tokens are not rotated.** A refresh answers with a new access
 *   token and no new refresh token; the one we hold stays good until the
 *   user revokes the app, it goes six months unused, or Google retires it.
 *   `invalid_grant` on a refresh is the one answer that means "the session
 *   is over"; anything else is the backend or the network, and the refresh
 *   token is kept for the next attempt.
 * - **Scopes are cumulative** (`include_granted_scopes=true`), so a sign-in
 *   that asks for one scope comes back with everything granted before, and
 *   the `scope` field of each token response says what the token holds.
 * - **Sign-in is a top-level redirect everywhere**, not a popup on desktop.
 *   A popup was only ever worth its trouble for the hourly renewal, which
 *   this file no longer needs; one code path beats two, and `/google-redirect`
 *   already carries the user back to where they were.
 */

import * as Sentry from '@sentry/browser';
import { googleClientId } from '../../config';
import { backendHost } from '../../settings';
import { updateGapiToken } from '../serviceWorker/serviceWorker';

export const clientID = googleClientId;

const authEndpoint = 'https://accounts.google.com/o/oauth2/v2/auth';

/**
 * Revocation is the one call that goes to Google directly: it takes no
 * secret, and Google's own client-side sample is a bare POST to it.
 */
const revokeEndpoint = 'https://oauth2.googleapis.com/revoke';

/** Our pass-through, not oauth2.googleapis.com/token - see above. */
export function tokenEndpoint() {
  return backendHost() + '/GoogleToken';
}

/**
 * Must match an entry in the OAuth client's Authorised redirect URIs byte for
 * byte - scheme, host, path, no trailing slash. Every origin the app is
 * served from needs registering, dev included, or sign-in fails there with
 * redirect_uri_mismatch.
 */
export const redirectPath = '/google-redirect';

export function redirectUri() {
  return window.location.origin + redirectPath;
}

/** What callers see: an access token and what it is good for. */
export interface GoogleToken {
  accessToken: string;
  /** Epoch ms. */
  expiresAt: number;
  /** Space separated, exactly as Google returned it. */
  scope: string;
}

/** What is stored: the token plus the means of getting the next one. */
interface GoogleSession extends GoogleToken {
  refreshToken?: string;
}

/**
 * `type` is the OAuth `error` code where Google or the backend gave one
 * (`invalid_grant` is the one the refresh path cares about), or one of this
 * module's own: `not_signed_in`, `backend_unreachable`.
 */
export class GoogleAuthError extends Error {
  constructor(
    message: string,
    public readonly type?: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'GoogleAuthError';
  }
}

/** A sign-in that failed on the way back, with somewhere to send the user. */
export class GoogleRedirectError extends Error {
  constructor(
    message: string,
    public readonly returnTo: string,
  ) {
    super(message);
    this.name = 'GoogleRedirectError';
  }
}

export interface RedirectIntent {
  /** Make Google Drive the storage provider once a token lands. */
  configureProvider?: boolean;
}

const sessionKey = 'googleAuth';
const pendingKey = 'googleAuthPending';

/**
 * What the implicit-flow era left in localStorage. Removed once at startup;
 * a token from there would have been an hour old at most anyway, and the
 * refresh token every session now needs can only come from a new sign-in.
 */
const legacyKeys = ['gisToken', 'gisGrantedScopes', 'gisLoginHint', 'gisRedirectState'];

/** A round trip that has taken longer than this is not one we still expect. */
const pendingLifetimeMs = 10 * 60 * 1000;

/** Renew this long before expiry, so in-flight requests keep working. */
const refreshGraceMs = 5 * 60 * 1000;

/**
 * How often to look at the clock while waiting for renewal to come due.
 *
 * One setTimeout for the whole 55 minutes was the first design, and it lost
 * a race it could not see: the token's expiry is wall-clock time, and a
 * timer is not. Sleep, a frozen tab, a throttled window - anything that
 * stops timers while the clock runs on - and the token has lapsed before the
 * timer ever fires. Short ticks fire promptly on wake, so the gap closes to
 * at most a tick.
 */
const refreshTickMs = 60 * 1000;

/**
 * How long a failed refresh stands before another is tried.
 *
 * A refresh that failed for any reason but `invalid_grant` is the backend
 * or the network, and asking again a second later changes nothing - except
 * the backend's rate limit, which a queue of forty tag refetches each
 * asking for a token would reach inside a minute. Until this passes, every
 * request fails at once with the last reason; a sign-in or a token Google
 * rejected outright clears it.
 */
const retryAfterFailureMs = 5 * 60 * 1000;

let session: GoogleSession | undefined;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let refreshInFlight: Promise<GoogleToken> | undefined;
let lastFailure: { err: GoogleAuthError; at: number } | undefined;

const listeners = new Set<(token: GoogleToken | undefined) => void>();

/** Notified whenever the token changes, including on expiry and revocation. */
export function onTokenChange(listener: (token: GoogleToken | undefined) => void) {
  listeners.add(listener);
  return function unsubscribe() {
    listeners.delete(listener);
  };
}

function publish() {
  const token = getToken();
  listeners.forEach((listener) => listener(token));
}

/* -------------------------------------------------------------------------
 * Token store
 * ---------------------------------------------------------------------- */

function readStoredSession(): GoogleSession | undefined {
  try {
    const raw = localStorage.getItem(sessionKey);

    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as GoogleSession;
    return parsed && parsed.accessToken ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function writeStoredSession(next: GoogleSession | undefined) {
  try {
    if (next) {
      localStorage.setItem(sessionKey, JSON.stringify(next));
    } else {
      localStorage.removeItem(sessionKey);
    }
  } catch {
    // Private browsing. The session still works for this page's lifetime;
    // it just will not survive a reload, and the user sees the button again.
  }
}

function isLive(candidate: GoogleSession | undefined): boolean {
  return !!candidate && !!candidate.accessToken && candidate.expiresAt > Date.now();
}

/** The caller's view: never the refresh token. */
function tokenOf(current: GoogleSession): GoogleToken {
  return {
    accessToken: current.accessToken,
    expiresAt: current.expiresAt,
    scope: current.scope,
  };
}

/**
 * Make a session the current one: the worker gets the access token for
 * <audio> Drive requests, which cannot set headers themselves; the store
 * gets the lot; the timer and the listeners hear about it.
 */
function activate(next: GoogleSession, { persist = true } = {}) {
  session = next;
  lastFailure = undefined;

  updateGapiToken(next.accessToken);

  if (persist) {
    writeStoredSession(next);
  }

  scheduleRefresh();
  publish();

  return tokenOf(next);
}

/**
 * Forget the session here and in storage. Google is not told; see
 * revokeSession for that.
 */
export function clearSession() {
  session = undefined;
  writeStoredSession(undefined);

  // The worker holds it too, and a dead token there means 401s on <audio>.
  updateGapiToken('');

  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = undefined;
  }

  publish();
}

/**
 * Withdraw the app's access at Google, the same as removing it from
 * https://myaccount.google.com/permissions. Only for a deliberate act: it
 * spends the consent, and the next sign-in is the full consent screen -
 * unverified-app interstitial and all.
 *
 * Revoking the refresh token revokes the whole grant, access tokens
 * included. The request is fire and forget: the store is already cleared,
 * and a revocation that did not reach Google leaves a grant the user can
 * still remove from their account page.
 */
export function revokeSession() {
  const token = session?.refreshToken || session?.accessToken;

  clearSession();
  lastFailure = undefined;

  if (!token) {
    return;
  }

  Promise.resolve()
    .then(() =>
      fetch(revokeEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: 'token=' + encodeURIComponent(token),
      }),
    )
    .catch(() => {});
}

let storageWatched = false;

/**
 * Adopt what another tab of this origin did.
 *
 * Two tabs each renew on their own clock, which Google allows - a refresh
 * token is not spent by use. But a logout in one tab should be a logout in
 * all of them, and a tab that has just renewed may as well share the hour
 * it got. localStorage fires `storage` in every *other* tab on a write.
 */
function watchStorage() {
  if (storageWatched || typeof window === 'undefined') {
    return;
  }

  storageWatched = true;

  window.addEventListener('storage', function adoptOtherTabsSession(event) {
    if (event.key !== sessionKey && event.key !== null) {
      return;
    }

    const stored = readStoredSession();

    if (!stored) {
      if (session) {
        // Logged out elsewhere. The store is already empty; do not write.
        session = undefined;
        updateGapiToken('');
        publish();
      }

      return;
    }

    if (!session || stored.expiresAt > session.expiresAt) {
      activate(stored, { persist: false });
    }
  });
}

/**
 * Adopt the session stored before this page loaded. Call once at startup,
 * before deciding whether the user is signed in.
 *
 * Returns a token only when the stored one is still live. An expired one
 * with a refresh token behind it is renewed in the background, and the
 * result reaches whoever subscribed through onTokenChange - and anything
 * that asks ensureToken() meanwhile joins that renewal rather than finding
 * nothing.
 */
export function restoreSession(): GoogleToken | undefined {
  watchStorage();

  try {
    legacyKeys.forEach((key) => localStorage.removeItem(key));
  } catch {}

  if (session) {
    return getToken();
  }

  const stored = readStoredSession();

  if (!stored) {
    return undefined;
  }

  if (isLive(stored)) {
    return activate(stored, { persist: false });
  }

  if (!stored.refreshToken) {
    writeStoredSession(undefined);
    return undefined;
  }

  session = stored;
  scheduleRefresh();
  ensureToken().catch(() => {
    // Logged inside refresh(). The timer keeps trying on its own schedule.
  });

  return undefined;
}

/** The current token, or undefined when absent or expired. */
export function getToken(): GoogleToken | undefined {
  return session && isLive(session) ? tokenOf(session) : undefined;
}

export function getAccessToken() {
  return getToken()?.accessToken;
}

export function hasScope(scope: string) {
  const token = getToken();
  return !!token && token.scope.split(' ').indexOf(scope) !== -1;
}

export function grantedScopes() {
  return getToken()?.scope ?? '';
}

/**
 * Whether there is a session at all, live token or not. The settings row
 * uses it to tell "signed out" from "signed in, renewing".
 */
export function hasSession() {
  return !!session && (isLive(session) || !!session.refreshToken);
}

/* -------------------------------------------------------------------------
 * Keeping it alive
 * ---------------------------------------------------------------------- */

async function postForm(body: string[][]) {
  let res: Response;

  try {
    res = await fetch(tokenEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body
        .map((pair) => encodeURIComponent(pair[0]) + '=' + encodeURIComponent(pair[1]))
        .join('&'),
    });
  } catch {
    throw new GoogleAuthError(
      'Could not reach the Fadila backend to renew Google access.',
      'backend_unreachable',
    );
  }

  const text = await res.text();
  let parsed: any;

  try {
    parsed = JSON.parse(text);
  } catch {
    parsed = undefined;
  }

  if (!res.ok) {
    throw new GoogleAuthError(
      (parsed && (parsed.error_description || parsed.error)) ||
        'Google rejected the token request (' + res.status + ').',
      parsed && parsed.error,
      res.status,
    );
  }

  return parsed;
}

function sessionFromPayload(payload: any, previous?: GoogleSession): GoogleSession {
  return {
    accessToken: payload.access_token,
    expiresAt: Date.now() + (payload.expires_in ?? 3600) * 1000,
    // A refresh names the scopes the new token holds; a code exchange with
    // include_granted_scopes names the union. Either way Google's word on
    // what this token can do, and the previous value only if it said nothing.
    scope: payload.scope || (previous && previous.scope) || '',
    // Google issues a refresh token on consent and never again; keep the one
    // we hold whenever the response carries none.
    refreshToken: payload.refresh_token || (previous && previous.refreshToken),
  };
}

function refresh(current: GoogleSession): Promise<GoogleToken> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  console.info(
    'Google renewal: asking the Fadila backend for a fresh access token.',
    'No window opens for this; the refresh token stands in for the sign-in.',
  );

  refreshInFlight = postForm([
    ['grant_type', 'refresh_token'],
    ['refresh_token', current.refreshToken!],
    ['client_id', clientID],
  ])
    .then(
      (payload) => {
        if (!payload || !payload.access_token) {
          throw new GoogleAuthError('Google returned no access token when renewing.');
        }

        return activate(sessionFromPayload(payload, current));
      },
      (err: Error) => {
        const failure =
          err instanceof GoogleAuthError ? err : new GoogleAuthError(err.message || String(err));

        if (failure.type === 'invalid_grant') {
          // The refresh token is dead: revoked at myaccount.google.com, six
          // months unused, or Google retired it. Nothing to renew with, and
          // the setup screen is where it starts again.
          console.warn(
            'Google renewal refused: the refresh token is no longer valid -',
            failure.message,
            '\n  The session is over; signing in again is needed.',
          );

          clearSession();

          lastFailure = {
            err: new GoogleAuthError('Google sign-in has expired. Sign in again.', 'invalid_grant'),
            at: Date.now(),
          };

          throw lastFailure.err;
        }

        console.warn(
          'Google renewal failed -',
          failure.type || 'no error type',
          '\n  reason:',
          failure.message,
          '\n  The refresh token is kept. Nothing asks the backend again for',
          retryAfterFailureMs / 60000,
          'minutes; until then anything needing Google fails with this reason.',
        );

        if (failure.type !== 'backend_unreachable') {
          Sentry.withScope((scope) => {
            scope.setExtra('type', failure.type);
            scope.setExtra('status', failure.status);
            scope.setExtra('reason', failure.message);
            Sentry.captureMessage('Google token refresh failed', Sentry.Severity.Warning);
          });
        }

        lastFailure = { err: failure, at: Date.now() };

        throw failure;
      },
    )
    .finally(() => {
      refreshInFlight = undefined;
    });

  return refreshInFlight;
}

function recentFailure(): GoogleAuthError | undefined {
  if (!lastFailure) {
    return undefined;
  }

  if (Date.now() - lastFailure.at >= retryAfterFailureMs) {
    lastFailure = undefined;
    return undefined;
  }

  return lastFailure.err;
}

/**
 * A token good for a request made now, renewing first if the one in hand has
 * lapsed.
 *
 * Every request path used to read getToken() and treat "undefined" as "never
 * signed in", which it is not: it is also what an hour-old token looks like
 * in the minute before the renewal timer reaches it. Requests bring the
 * renewal forward instead of failing on the timer's schedule.
 *
 * Rejects with `not_signed_in` when there is nothing to renew with, with
 * `invalid_grant` when Google has ended the session, and with whatever the
 * backend said otherwise.
 */
export function ensureToken(): Promise<GoogleToken> {
  const token = getToken();

  if (token) {
    return Promise.resolve(token);
  }

  if (refreshInFlight) {
    return refreshInFlight;
  }

  // Checked before the session is, so that the five minutes after Google
  // ended one report that rather than a bare "not signed in".
  const failed = recentFailure();

  if (failed) {
    return Promise.reject(failed);
  }

  if (!session || !session.refreshToken) {
    return Promise.reject(new GoogleAuthError('Not signed in to Google.', 'not_signed_in'));
  }

  return refresh(session);
}

/**
 * Google rejected the token we hold with a 401. Renew now, regardless of
 * any recent failure: a token Google itself refused is a stronger signal
 * than a backend that was unreachable a minute ago.
 *
 * Rejects when there is nothing to renew with or the renewal fails; the
 * caller decides whether that ends the session.
 */
export function renewAfterRejection(): Promise<GoogleToken> {
  if (!session || !session.refreshToken) {
    clearSession();
    return Promise.reject(new GoogleAuthError('Not signed in to Google.', 'not_signed_in'));
  }

  if (refreshInFlight) {
    return refreshInFlight;
  }

  // Expire it here, so nothing hands it out while the renewal is in flight.
  session = { ...session, expiresAt: 0 };
  lastFailure = undefined;

  return refresh(session);
}

/**
 * Arrange a renewal shortly before expiry.
 *
 * Ticks rather than sleeps until then - see refreshTickMs for the race the
 * single long timer lost - and shares the renewal itself with ensureToken,
 * so a request that arrives first simply brings it forward.
 */
function scheduleRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = undefined;
  }

  if (!session || !session.refreshToken) {
    return;
  }

  const due = session.expiresAt - refreshGraceMs;
  const wait = Math.min(Math.max(due - Date.now(), 1000), refreshTickMs);

  refreshTimer = setTimeout(function tick() {
    refreshTimer = undefined;

    if (!session || !session.refreshToken) {
      return;
    }

    // Not yet, or a failure is still fresh: look again in a bit. The
    // comparison is against the clock, not against how many ticks have
    // passed, so a tick that arrives late after a sleep still does the
    // right thing.
    if (session.expiresAt - refreshGraceMs > Date.now() || recentFailure()) {
      scheduleRefresh();
      return;
    }

    // Success re-schedules through activate; failure is logged inside, and
    // the next tick sees lastFailure until the retry interval has passed.
    refresh(session)
      .catch(() => {})
      .finally(() => {
        if (!refreshTimer) {
          scheduleRefresh();
        }
      });
  }, wait);
}

/* -------------------------------------------------------------------------
 * PKCE
 * ---------------------------------------------------------------------- */

function randomUrlSafe(byteLength: number) {
  const bytes = new Uint8Array(byteLength);

  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  return Array.prototype.map.call(bytes, (b: number) => ('0' + b.toString(16)).slice(-2)).join('');
}

function base64UrlEncode(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * S256. crypto.subtle needs a secure context, and everything this app runs
 * in is one - production is HTTPS and the dev server is forced to HTTPS - so
 * this should never throw. If it does, the message says what is missing.
 */
async function deriveChallenge(verifier: string) {
  if (!window.crypto || !window.crypto.subtle) {
    throw new Error(
      'Google sign-in needs Web Crypto, which this browser does not provide here. ' +
        'Open Fadila over HTTPS and try again.',
    );
  }

  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));

  return base64UrlEncode(digest);
}

/* -------------------------------------------------------------------------
 * Starting the flow
 * ---------------------------------------------------------------------- */

interface PendingAuth {
  state: string;
  verifier: string;
  returnTo: string;
  intent: RedirectIntent;
  createdAt: number;
}

/**
 * localStorage rather than sessionStorage on purpose. iOS may service the
 * cross-origin hop by suspending or even killing the standalone app, and a
 * relaunch starts a fresh session - which would strand the flow at exactly
 * the point it is meant to fix. The value is a CSRF nonce, a PKCE verifier
 * and a path, so it keeps badly without consequence.
 */
function readPending(): PendingAuth | undefined {
  try {
    const raw = localStorage.getItem(pendingKey);

    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as PendingAuth;

    if (!parsed || !parsed.state || Date.now() - parsed.createdAt > pendingLifetimeMs) {
      return undefined;
    }

    return parsed;
  } catch {
    return undefined;
  }
}

function clearPending() {
  try {
    localStorage.removeItem(pendingKey);
  } catch {}
}

async function buildAuthUrl(scope: string, intent: RedirectIntent) {
  const state = randomUrlSafe(16);
  // 32 bytes hex-encoded is 64 characters, inside the 43-128 range PKCE
  // allows, and hex digits are within its alphabet.
  const verifier = randomUrlSafe(32);

  try {
    localStorage.setItem(
      pendingKey,
      JSON.stringify({
        state,
        verifier,
        // Come back to wherever the user actually was, not a fixed landing page.
        returnTo: window.location.pathname + window.location.search,
        intent,
        createdAt: Date.now(),
      } as PendingAuth),
    );
  } catch {
    // Private browsing. The trip out still works; the trip back will fail the
    // state check and say so, which beats navigating away silently.
  }

  const challenge = await deriveChallenge(verifier);

  const params = [
    ['client_id', clientID],
    ['redirect_uri', redirectUri()],
    ['response_type', 'code'],
    ['scope', scope],
    ['state', state],
    ['code_challenge', challenge],
    ['code_challenge_method', 'S256'],
    // The refresh token. Without this Google issues access tokens only.
    ['access_type', 'offline'],
    // And only alongside a consent screen - see the file comment.
    ['prompt', 'consent'],
    // Asking for one scope must not drop the rest.
    ['include_granted_scopes', 'true'],
  ]
    .map((pair) => encodeURIComponent(pair[0]) + '=' + encodeURIComponent(pair[1]))
    .join('&');

  return authEndpoint + '?' + params;
}

/**
 * Navigates away. Nothing after this call runs, so the promise it returns
 * never settles - by design, exactly as beginRedirect does for the other
 * providers. Anything that has to happen after sign-in goes in `intent` and
 * is replayed once the token lands.
 */
export async function beginSignIn({
  scope,
  intent = {},
}: {
  scope: string;
  intent?: RedirectIntent;
}): Promise<never> {
  const authUrl = await buildAuthUrl(scope, intent);

  console.info(
    'Google sign-in: leaving for Google to ask for scopes',
    scope,
    '- the consent screen shows every time, because that is what Google issues a refresh token with.',
  );

  // assign, not replace: leaves the app in history so back does something sane.
  window.location.assign(authUrl);

  return new Promise<never>(() => {});
}

/* -------------------------------------------------------------------------
 * Finishing the flow
 * ---------------------------------------------------------------------- */

function parseParams(str: string) {
  const out: { [key: string]: string } = {};

  str
    .replace(/^[#?]/, '')
    .split('&')
    .forEach(function readPair(pair) {
      if (!pair) {
        return;
      }

      const idx = pair.indexOf('=');
      const key = decodeURIComponent(idx === -1 ? pair : pair.slice(0, idx));

      out[key] = idx === -1 ? '' : decodeURIComponent(pair.slice(idx + 1).replace(/\+/g, ' '));
    });

  return out;
}

/**
 * What the user was in the middle of when they were sent to Google. Held in
 * module state once the trip is complete, because the redirect route hands
 * back to the app through the router, which keeps this JS context alive. A
 * full reload would lose it, and that is a fair trade - the worst case is
 * one extra tap.
 */
let redirectIntent: RedirectIntent | undefined;

/** Read once, by whoever acts on it. */
export function consumeRedirectIntent(): RedirectIntent | undefined {
  const intent = redirectIntent;
  redirectIntent = undefined;
  return intent;
}

export interface SignInResult {
  returnTo: string;
  intent: RedirectIntent;
}

/**
 * Exchanges the code Google handed back and stores the session. Runs on
 * /google-redirect, which is the app itself mid-navigation.
 *
 * Throws GoogleRedirectError carrying a usable returnTo, so a failure can
 * still put the user back in the app rather than stranding them on a blank
 * callback page.
 */
export async function completeSignIn(search: string): Promise<SignInResult> {
  const pending = readPending();
  const returnTo = (pending && pending.returnTo) || '/app';

  clearPending();

  const params = parseParams(search);

  if (params.error) {
    throw new GoogleRedirectError(params.error_description || params.error, returnTo);
  }

  if (!pending) {
    throw new GoogleRedirectError(
      'Google sign-in could not be completed - the request it answers has expired.',
      returnTo,
    );
  }

  if (!params.state || params.state !== pending.state) {
    throw new GoogleRedirectError('Google sign-in returned an unexpected response.', returnTo);
  }

  if (!params.code) {
    throw new GoogleRedirectError('Google sign-in returned no authorisation code.', returnTo);
  }

  let payload: any;

  try {
    payload = await postForm([
      ['grant_type', 'authorization_code'],
      ['code', params.code],
      ['redirect_uri', redirectUri()],
      ['client_id', clientID],
      ['code_verifier', pending.verifier],
    ]);
  } catch (err) {
    throw new GoogleRedirectError((err as Error).message, returnTo);
  }

  if (!payload || !payload.access_token) {
    throw new GoogleRedirectError('Google returned no access token.', returnTo);
  }

  const previous = session || readStoredSession();
  const next = sessionFromPayload(payload, previous);

  if (!next.refreshToken) {
    // prompt=consent should make this impossible. If it happens anyway, the
    // hour of access is real but the renewal will not be, and the user meets
    // the button again - say so where someone can find it.
    console.warn(
      'Google sign-in returned no refresh token. Access lasts an hour and cannot be renewed quietly.',
    );
    Sentry.captureMessage('Google sign-in returned no refresh token', Sentry.Severity.Warning);
  }

  activate(next);

  redirectIntent = pending.intent || {};

  return { returnTo, intent: redirectIntent };
}
